import type { CaseImageModel, CaseImageType } from '@onrunning/backstage-models';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDocumentHelpers } from '@/composables/document-helpers';
import { useAsyncQueue } from '@/composables/useAsyncQueue';
import { useImageHelper } from '@/composables/useImageHelper';
import { useNotification } from '@/composables/useNotification';
import { imageSlotConfig, productImageSlots } from '@/constants/case.constants';
import type { CaseImage } from '@/models/case-image.model';
import type { CaseLine } from '@/models/case-line.model';
import { CasesService } from '@/services/cases.service';
import { useUserStore } from '@/stores/user';
import { ImageSlotType } from '@/types/case.enum';
import { type CaseLineSlotImage, type CaseLineSlots } from '@/types/case.interface';

const MAX_IMAGES = 6;

export function useCaseLineImages(
  line: Ref<CaseLine>,
  caseId: Ref<string>,
  onUpdateLine: (updatedLine: CaseLine) => void,
) {
  const { t } = useI18n();
  const userStore = useUserStore();
  const { notifyError } = useNotification();
  const { reEncodeImage } = useImageHelper();
  const { generateFileSHA256 } = useDocumentHelpers();

  const casesService = new CasesService();

  const { isProcessing: saving, enqueue: queueUpdate } = useAsyncQueue();

  const lineImageMap = ref<CaseLineSlots>({ required: new Map(), optional: new Map() });
  const lineImageHashes = ref<Map<string, string>>(new Map());

  const minImageCount = computed(() => productImageSlots.get(line.value.productMainGroup)?.size ?? 0);

  const initializeLineImagesMap = (): CaseLineSlots => {
    const requiredCount = minImageCount.value;
    const slotConfig = productImageSlots.get(line.value.productMainGroup) ?? new Map();
    const required = new Map<number, CaseLineSlotImage>();
    const optional = new Map<number, CaseLineSlotImage>();

    for (let index = 1; index <= MAX_IMAGES; index++) {
      const imageType = slotConfig.get(index) as CaseImageType;
      const isRequired = index <= requiredCount;

      const meta: CaseLineSlotImage = {
        imageType,
        required: isRequired,
        backgroundImage: imageSlotConfig[imageType]?.background,
        label: isRequired ? t(`viewCase.images.${imageSlotConfig[imageType]?.label}_label`) : undefined,
      };

      (isRequired ? required : optional).set(index, meta);
    }

    return { required, optional };
  };

  const mapRequiredImage = (image: CaseImageModel, map: CaseLineSlots) => {
    const slot = imageSlotConfig[image.imageType!]?.position;
    if (!slot) return;
    const existing = map.required.get(slot)!;
    map.required.set(slot, { ...existing, imageUrl: image.imageUrl, imageId: image.imageId });
  };

  const mapOptionalImage = (image: CaseImageModel, map: CaseLineSlots) => {
    const requiredCount = minImageCount.value;
    for (let i = requiredCount + 1; i <= MAX_IMAGES; i++) {
      const existing = map.optional.get(i)!;
      if (!existing.imageUrl) {
        map.optional.set(i, { ...existing, imageUrl: image.imageUrl, imageId: image.imageId });
        break;
      }
    }
  };

  const setLineImageMap = (image: CaseImageModel, map: CaseLineSlots) => {
    image.imageType ? mapRequiredImage(image, map) : mapOptionalImage(image, map);
  };

  const initializeImages = async (): Promise<void> => {
    if (!line.value) {
      return;
    }

    const imageHashes = new Map<string, string>();
    const imageMap = initializeLineImagesMap();

    for (const image of line.value.images || []) {
      try {
        const imgSHA256 = await generateFileSHA256(image.imageUrl);
        imageHashes.set(image.imageId, imgSHA256);
      } catch (error: any) {
        window.newrelic.noticeError(error, {
          message: 'Error generating file hash for case line image',
          details: {
            lineId: line.value.id,
            imageId: image.imageId,
          },
        });
      }

      setLineImageMap(image, imageMap);
    }

    lineImageMap.value = imageMap;
    lineImageHashes.value = imageHashes;
  };

  const isImageSimilar = (imgSHA256: string): boolean => {
    const isTooSimilar = [...lineImageHashes.value.values()].includes(imgSHA256);

    if (isTooSimilar) {
      notifyError(t('viewCase.duplicate_image_error'));
    }

    return isTooSimilar;
  };

  const onChangeImage = async (imageFile: File, index: number) => {
    try {
      const encodedImage = await reEncodeImage(imageFile);
      const imgSHA256 = await generateFileSHA256(encodedImage);

      if (isImageSimilar(imgSHA256)) {
        return;
      }

      const imageType = productImageSlots.get(line.value.productMainGroup)?.get(index);

      await queueUpdate(async () => {
        const response = await casesService.addLineImage(
          caseId.value,
          line.value,
          encodedImage,
          userStore.selectedAccount.id,
          imageType,
        );

        if (response.model) {
          const image = response.model;

          lineImageHashes.value.set(image.imageId, imgSHA256);
          setLineImageMap(image, lineImageMap.value);
          onUpdateLine({ ...line.value, images: [...(line.value.images || []), image] });
        }
      });
    } catch (error) {
      notifyError(t('viewCase.image_upload_error'));

      return;
    }
  };

  const deleteImage = async (image: CaseLineSlotImage, index: number, type: ImageSlotType) => {
    await queueUpdate(async () => {
      const response = await casesService.deleteLineImage(
        caseId.value,
        line.value,
        image.imageId!,
        userStore.selectedAccount.id,
      );

      if (response.isValid()) {
        lineImageHashes.value?.delete(image.imageId!);

        const lineImages = lineImageMap.value?.[type];
        const lineImage = lineImages?.get(index);

        lineImages.set(index, {
          ...lineImage,
          ...{ imageUrl: undefined, imageId: undefined },
        });

        const images = line.value.images?.filter((obj: CaseImage) => obj.imageId !== image.imageId);

        onUpdateLine({ ...line.value, images: images || [] });
      }
    });
  };

  return {
    // State
    saving,
    lineImageMap,

    // Computed
    minImageCount,

    // Actions
    initializeImages,
    onChangeImage,
    deleteImage,
  };
}
